<script setup lang="ts">
import type {
  INotification,
  INotificationAction,
} from '~/interfaces/notifications/notifications';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { useNotifications } from '~/composables/useStates/useNotifications';
import NotificationsNotificationContextMenu from '~/components/marketing/notifications/notifications-notification-context-menu.vue';

interface Props {
  notification: INotification;
  active: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'active', payload: { id: string }): void;
}>();

const { $trackEvent } = useNuxtApp();
const { getPlaceholder } = useBranding();
const { panelOpen, updateByIds, timeSinceNotification } = useNotifications();

const open = ref<boolean>(false);
const notificationRef = ref<HTMLElement | null>(null);
const rightClicked = ref<boolean>(false);

const isUnread = computed(() => !props.notification.isRead);
const hasImage = computed(
  () => !!props.notification.imageURL || !!props.notification.imageUrl,
);
const actions = computed(() => props.notification.notificationActions || []);

const isCoupon = computed(
  () => props.notification.notificationStyle === 'Coupon',
);

const toggleOpen = () => {
  open.value = !open.value;
  if (!props.notification.isRead && !isCoupon.value)
    updateByIds({
      isRead: true,
      ids: [props.notification.notificationId],
      updateType: 'Status',
    });
  emit('active', { id: props.notification.notificationId });
};

const handleAction = (action: INotificationAction) => {
  open.value = false;
  panelOpen.value = false;
  useMessageHandler(
    action as unknown as { action: string; actionValue: string },
  );
  $trackEvent('Notification', 'NOTIFICATION_ACTION_CLICK', {
    action: action.action,
    value: action.actionValue,
  });
};

watchEffect(() => {
  if (!props.active) open.value = false;
  if (open.value)
    nextTick(() => {
      setTimeout(() => {
        notificationRef.value?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }, 200);
    });
});

const contextMenuOptions = computed(() => [
  {
    label: `mark-as-${props.notification.isRead ? 'unread' : 'read'}`,
    action: async () => {
      $trackEvent(
        'Notification',
        props.notification.isRead
          ? 'NOTIFICATION_MARK_AS_UNREAD'
          : 'NOTIFICATION_MARK_AS_READ',
        { id: props.notification.notificationId },
      );
      updateByIds({
        isRead: !props.notification.isRead,
        ids: [props.notification.notificationId],
        updateType: 'Status',
      });
    },
  },
  {
    label: `delete`,
    action: async () => {
      updateByIds({
        ids: [props.notification.notificationId],
        updateType: 'Delete',
      });
      $trackEvent('Notification', 'NOTIFICATION_DELETE', {
        id: props.notification.notificationId,
      });
    },
    requiresConfirm: true,
    confirmKeys: {
      titleKey: 'delete-notification-confirm',
      confirmKey: 'delete',
      cancelKey: 'cancel',
    },
  },
]);

const handleCouponClick = () => {
  open.value = false;
  panelOpen.value = false;
  $trackEvent('Account', 'MY_ACCOUNT_SETTINGS', { to: 'Gifts & Offers' });
};
</script>

<template>
  <article
    ref="notificationRef"
    class="rounded-2xl bg-base pt-2 text-base text-base-priority shadow-sm cursor-pointer"
    role="group"
    :aria-label="notification.messageHeader"
    @click="toggleOpen"
    @contextmenu.prevent="rightClicked = true"
  >
    <header class="flex items-start justify-between px-2 gap-3">
      <div class="flex items-center justify-center place-self-center gap-2">
        <span class="text-sm">
          {{ timeSinceNotification(notification.notificationDate) }}
        </span>
        <span
          v-if="isUnread"
          class="inline-block h-2 w-2 rounded-full bg-primary-blue-gradient"
          aria-label="unread"
        />
      </div>
      <NotificationsNotificationContextMenu
        v-if="!notification.isGlobal && !isCoupon"
        v-model="rightClicked"
        :menu-options="contextMenuOptions"
      />
    </header>

    <div
      v-if="!isCoupon"
      class="my-2 flex items-center justify-between gap-3 px-2"
    >
      <div class="flex items-center gap-2">
        <LazyDesignSystemLazyImage
          :src="getPlaceholder('contentCardPlaceholder')"
          alt="notification icon"
          class="h-10 w-10 !min-h-10 !min-w-10 rounded-md object-contain text-transparent"
        />
        <span class="font-semibold text-xs">
          {{ notification.messageHeader }}
        </span>
      </div>
      <DesignSystemButton
        arial-label="More options"
        type="transparent"
        padding="sm"
        :aria-expanded="open"
        :aria-controls="`notification-body-${notification.notificationId}`"
      >
        <ChevronDownIcon
          :class="[
            '!w-5 !h-5 stroke-3 transition-transform duration-300 ease-in-out',
            {
              '-rotate-180': open,
            },
          ]"
        />
      </DesignSystemButton>
    </div>

    <Transition name="scale">
      <section
        v-if="open || isCoupon"
        :id="`notification-body-${notification.notificationId}`"
        class="overflow-hidden"
        :aria-hidden="!open"
      >
        <div v-if="!isCoupon" class="space-y-3 px-2">
          <LazyDesignSystemLazyImage
            v-if="hasImage"
            :src="notification.imageURL || notification.imageUrl"
            alt=""
            class="w-full rounded-lg object-cover"
            :placeholder="getPlaceholder('promotionsPlaceholder')"
          />

          <p
            class="text-sm text-secondary leading-5"
            v-html="notification.messageBody"
          />

          <div class="flex items-center justify-center gap-2 pb-2">
            <DesignSystemButton
              v-for="(a, idx) in actions"
              :key="idx"
              class="w-full items-center justify-center"
              @click.stop="handleAction(a)"
            >
              {{ a.actionText }}
            </DesignSystemButton>
          </div>
        </div>
        <MarketingNotificationsCoupon
          v-else
          :notification="notification"
          @click="handleCouponClick"
        />
      </section>
    </Transition>
  </article>
</template>
<style scoped lang="scss">
.scale-enter-active,
.scale-leave-active {
  @apply transition-all duration-200 ease-in-out;
}
.scale-enter-from,
.scale-leave-to {
  @apply h-0 opacity-0;
}
.scale-enter-to,
.scale-leave-from {
  @apply h-auto opacity-100;
}
</style>
